import { envs } from '@/envs'
import { isCordovaApp } from '@/helpers'

export const TICKET_ATTACHMENTS_MAX_FILES = 5
export const TICKET_ATTACHMENTS_MAX_SIZE_BYTES = 5 * 1024 * 1024

const TICKET_ALLOWED_MIME_TYPES = new Set([
  // Text / documents
  'text/plain',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint.presentation.macroenabled.12',
  'application/vnd.openxmlformats-officedocument.presentationml.template',
  'application/x-mspublisher',

  // OpenOffice / OpenDocument
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.graphics',
  'application/vnd.oasis.opendocument.database',
  'application/vnd.oasis.opendocument.formula',
  'application/vnd.oasis.opendocument.text-master',
  'application/vnd.oasis.opendocument.text-template',
  'application/vnd.oasis.opendocument.spreadsheet-template',
  'application/vnd.oasis.opendocument.presentation-template',
  'application/vnd.oasis.opendocument.graphics-template',

  // Images
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/svg+xml',
  'image/avif',
  'image/x-raw',
  'image/x-adobe-dng',
  'image/x-canon-cr2',
  'image/x-nikon-nef',
  'image/x-sony-arw',
  'image/x-fuji-raf',
  'image/x-panasonic-rw2',
  'image/x-pentax-pef',
  'image/x-olympus-orf',

  // Video
  'video/mp4',
  'video/quicktime',
  'video/x-matroska',
  'video/x-msvideo',
  'video/webm',
  'video/3gpp',
  'video/hevc',
  'video/h265',
  'video/av1',

  // Audio
  'audio/mpeg',
  'audio/aac',
  'audio/mp4',
  'audio/x-m4a',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/flac',
  'audio/ogg',
  'audio/opus',
  'audio/amr',
  'audio/3gpp',
])

const TICKET_ALLOWED_EXTENSIONS = new Set([
  // Text / docs
  'txt',
  'pdf',
  'doc',
  'docx',
  'dot',
  'dotx',
  'xls',
  'xlsx',
  'xltx',
  'ppt',
  'pptx',
  'pptm',
  'potx',
  'pub',

  // OpenOffice / OpenDocument
  'odt',
  'ods',
  'odp',
  'odg',
  'odb',
  'odf',
  'odm',
  'ott',
  'ots',
  'otp',
  'otg',

  // Images
  'jpeg',
  'jpg',
  'png',
  'webp',
  'heic',
  'heif',
  'gif',
  'bmp',
  'tiff',
  'tif',
  'svg',
  'avif',
  'raw',
  'dng',
  'cr2',
  'nef',
  'arw',
  'raf',
  'rw2',
  'pef',
  'orf',
  'sr2',

  // Video
  'mp4',
  'mov',
  'mkv',
  'avi',
  'webm',
  '3gp',
  'hevc',
  'h265',
  'av1',

  // Audio
  'mp3',
  'aac',
  'm4a',
  'wav',
  'flac',
  'ogg',
  'opus',
  'amr',
])

export const TICKET_ATTACHMENTS_ACCEPT = Array
  .from(TICKET_ALLOWED_EXTENSIONS)
  .map((ext) => `.${ext}`)
  .join(',')

export type T_TicketUploadedFile = {
  id: number
  title?: string
  path?: string
}

export type T_TicketAttachmentDraft = T_TicketUploadedFile & {
  localId: string
  name: string
  size: number
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const extractUploadList = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value
  if (!isRecord(value)) return []

  if (Array.isArray(value.list)) return value.list

  const data = value.data
  if (isRecord(data) && Array.isArray(data.list)) {
    return data.list
  }

  return []
}

export const extractUploadedTicketFile = (value: unknown): T_TicketUploadedFile | null => {
  const [ first ] = extractUploadList(value)
  if (!isRecord(first)) return null

  const rawId = first.id
  const id = typeof rawId === 'number' ? rawId : Number(rawId)
  if (!Number.isFinite(id) || id <= 0) return null

  return {
    id,
    title: typeof first.title === 'string' ? first.title : undefined,
    path: typeof first.path === 'string' ? first.path : undefined,
  }
}

export const createAttachmentLocalId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

export const isTicketAttachmentAllowed = (file: File): boolean => {
  const mimeType = (file.type || '').toLowerCase()
  const extension = (file.name.split('.').pop() || '').toLowerCase()

  // Step 1: MIME check
  if (mimeType && TICKET_ALLOWED_MIME_TYPES.has(mimeType)) {
    return true
  }

  // Step 2: fallback by extension (for empty/inaccurate MIME)
  return TICKET_ALLOWED_EXTENSIONS.has(extension)
}

export const resolveTicketFileUrl = (rawPath?: string): string | null => {
  if (!rawPath) return null

  if (/^https?:\/\//i.test(rawPath)) return rawPath
  if (rawPath.startsWith('//')) return `https:${rawPath}`
  const baseUrl = envs.API_DOMAIN || ''
  if (!baseUrl) return rawPath

  try {
    return new URL(rawPath, baseUrl).toString()
  } catch {
    return rawPath
  }
}

const sanitizeFileName = (rawName: string): string => {
  return rawName
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
    .trim()
}

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const getFileNameFromUrl = (fileUrl: string): string => {
  try {
    const parsed = new URL(fileUrl)
    const lastChunk = parsed.pathname.split('/').pop() || ''
    return safeDecodeURIComponent(lastChunk)
  } catch {
    const normalized = fileUrl.split('?')[0] || ''
    return safeDecodeURIComponent(normalized.split('/').pop() || '')
  }
}

const triggerBrowserDownload = (href: string, filename?: string) => {
  if (typeof document === 'undefined') return

  const link = document.createElement('a')
  link.href = href
  link.rel = 'noopener noreferrer'
  link.style.display = 'none'
  if (filename) {
    link.download = filename
  }

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const openCordovaSystemBrowserForDownload = (fileUrl: string): boolean => {
  if (!isCordovaApp) return false
  if (typeof window === 'undefined') return false

  const inAppBrowserOpen = (window as Window & {
    cordova?: {
      InAppBrowser?: {
        open?: (url: string, target?: string, options?: string) => unknown
      }
    }
  }).cordova?.InAppBrowser?.open

  if (typeof inAppBrowserOpen !== 'function') return false

  try {
    inAppBrowserOpen(fileUrl, '_system')
    return true
  } catch (error) {
    console.warn('[attachments] failed to open system browser fallback', error)
    return false
  }
}

export type T_DownloadTicketFileResult =
  | 'web'
  | 'cordova-system-browser'

export const downloadTicketFile = async (
  fileUrl: string,
  fileName?: string,
): Promise<T_DownloadTicketFileResult> => {
  if (!fileUrl) {
    throw new Error('File URL is empty')
  }

  const rawResolvedName = fileName || getFileNameFromUrl(fileUrl) || 'attachment'
  const resolvedName = sanitizeFileName(rawResolvedName) || 'attachment'

  if (isCordovaApp) {
    if (openCordovaSystemBrowserForDownload(fileUrl)) {
      return 'cordova-system-browser'
    }

    throw new Error('Unable to open system browser for file download in Cordova')
  }

  try {
    const response = await fetch(fileUrl, { credentials: 'include' })
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status}`)
    }

    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    triggerBrowserDownload(blobUrl, resolvedName)
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000)
    return 'web'
  } catch (error) {
    console.warn('[attachments] blob download failed, fallback to direct link', error)
  }

  triggerBrowserDownload(fileUrl, resolvedName)
  return 'web'
}

export const formatAttachmentSize = (size: number): string => {
  if (size < 1024) return `${size} B`

  const kb = size / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`

  return `${(kb / 1024).toFixed(1)} MB`
}
